import { Spinner } from "@via/components/loading/loading.tsx";
import {
  useAuditHealth,
  useVerifyAuditChain,
} from "@via/pages/account/admin/admin.api.tsx";
import { AuditHealthResponse } from "common";

type Tone = "ok" | "warn" | "bad" | "off";

type Row = { label: string; tone: Tone; status: string; detail?: string };

const TONE_CLASSES: Record<Tone, string> = {
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  warn: "bg-amber-50 text-amber-800 ring-amber-600/20",
  bad: "bg-rose-50 text-rose-700 ring-rose-600/20",
  off: "bg-gray-50 text-gray-600 ring-gray-500/10",
};

const when = (iso: string) => new Date(iso).toLocaleString();

const BREAK_REASONS: Record<string, string> = {
  hash_mismatch: "a record was changed",
  prev_hash_mismatch: "a record does not follow the one before it",
  seq_gap: "records are missing",
  file_gap: "a day of records is missing",
  unparseable: "a record cannot be read",
};

export const chainRow = (chain: AuditHealthResponse["chainVerified"]): Row => {
  const label = "Audit records verified";
  if (!chain)
    return {
      label,
      tone: "warn",
      status: "Not checked yet",
      detail: "The first check runs a minute after the backend starts.",
    };
  if (chain.ok)
    return {
      label,
      tone: "ok",
      status: "Verified",
      detail: `${chain.lines} records across ${chain.files} days, checked ${when(chain.checkedAt)}`,
    };
  if (chain.unreadable)
    return {
      label,
      tone: "bad",
      status: "Could not read",
      detail: `The audit files could not be read, checked ${when(chain.checkedAt)}`,
    };
  if (chain.files === 0)
    return {
      label,
      tone: "warn",
      status: "No audit files",
      detail: `None found, checked ${when(chain.checkedAt)}`,
    };
  const found = chain.firstBreak
    ? `${BREAK_REASONS[chain.firstBreak.reason] ?? "the chain is broken"} in ${chain.firstBreak.file}${chain.firstBreak.seq === null ? "" : ` at record ${chain.firstBreak.seq}`}`
    : "the chain is broken";
  return {
    label,
    tone: "bad",
    status: "Failed",
    detail: `Found that ${found}, checked ${when(chain.checkedAt)}`,
  };
};

const SINK_NAMES: Record<string, string> = {
  audit_syslog: "syslog",
  audit_s3: "S3",
};

/** A ship older than this is shown as a warning: S3 batches hourly by default. */
const STALE_SHIP_MS = 2 * 60 * 60 * 1000;
/** How long a sink that recovered from an error keeps a warning: that copy may miss a record. */
const RECENT_ERROR_MS = 24 * 60 * 60 * 1000;

const sinkRow = (
  sink: AuditHealthResponse["shipper"]["sinks"][number],
  reachable: boolean | null,
  now: number
): Row => {
  const label = `Last shipped to ${SINK_NAMES[sink.id] ?? sink.id}`;
  const failing =
    sink.lastErrorAt !== null &&
    (sink.lastShipAt === null || sink.lastErrorAt > sink.lastShipAt);
  if (failing)
    return {
      label,
      tone: "bad",
      status: "Failing",
      detail: `Errors since the last successful send, the latest at ${when(sink.lastErrorAt as string)}`,
    };
  if (!sink.lastShipAt)
    return {
      label,
      tone: "warn",
      status: "Nothing seen yet",
      detail: "Nothing shipped since the backend started watching.",
    };
  if (
    sink.lastErrorAt !== null &&
    now - Date.parse(sink.lastErrorAt) < RECENT_ERROR_MS
  )
    return {
      label,
      tone: "warn",
      status: when(sink.lastShipAt),
      detail: `Shipping again after an error at ${when(sink.lastErrorAt)}. A record may be missing from this copy; check it with the audit verify command.`,
    };
  if (reachable === false)
    return {
      label,
      tone: "warn",
      status: when(sink.lastShipAt),
      detail: "The shipper does not answer now, so this may be out of date.",
    };
  if (now - Date.parse(sink.lastShipAt) > STALE_SHIP_MS)
    return {
      label,
      tone: "warn",
      status: when(sink.lastShipAt),
      detail:
        "Over two hours ago. Expected only when no audit records were written since.",
    };
  return { label, tone: "ok", status: when(sink.lastShipAt) };
};

export const shipperRows = (
  shipper: AuditHealthResponse["shipper"],
  now: number = Date.now()
): Row[] => {
  if (!shipper.enabled)
    return [
      {
        label: "Audit shipper",
        tone: "off",
        status: "Not enabled",
        detail: "Audit records are kept on this server only.",
      },
    ];
  const reachable: Row =
    shipper.reachable === true
      ? { label: "Audit shipper", tone: "ok", status: "Reachable" }
      : shipper.reachable === false
        ? {
            label: "Audit shipper",
            tone: "bad",
            status: "Unreachable",
            detail: "Records may not be leaving this server.",
          }
        : { label: "Audit shipper", tone: "warn", status: "Not checked yet" };
  if (shipper.sinks.length === 0)
    return [
      reachable,
      {
        label: "Last shipped",
        tone: "warn",
        status: "Nothing seen yet",
        detail: shipper.observedSince
          ? `Nothing shipped since ${when(shipper.observedSince)}`
          : undefined,
      },
    ];
  return [
    reachable,
    ...shipper.sinks.map(sink => sinkRow(sink, shipper.reachable, now)),
  ];
};

export const writeFailureRow = (
  failures: AuditHealthResponse["writeFailures"]
): Row =>
  failures.count === 0
    ? { label: "Audit write failures", tone: "ok", status: "None" }
    : {
        label: "Audit write failures",
        tone: "bad",
        status: `${failures.count} since the backend started`,
        detail: failures.lastAt
          ? `Last at ${when(failures.lastAt)}`
          : undefined,
      };

const AuditHealthCard = () => {
  const health = useAuditHealth();
  const verify = useVerifyAuditChain();

  const rows: Row[] = health.data
    ? [
        chainRow(health.data.chainVerified),
        ...shipperRows(health.data.shipper),
        writeFailureRow(health.data.writeFailures),
      ]
    : [];

  return (
    <section aria-labelledby="audit-health-heading">
      <div className="flex items-center justify-between mb-3">
        <h3
          id="audit-health-heading"
          className="text-sm font-medium text-gray-900">
          Audit records
        </h3>
        <button
          type="button"
          onClick={() => verify.mutate()}
          disabled={verify.isPending || health.isLoading}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed">
          <Spinner loading={verify.isPending}>Verify now</Spinner>
        </button>
      </div>
      {health.isLoading && <Spinner loading>Loading</Spinner>}
      {health.isError && (
        <p className="text-sm text-rose-700">
          Could not load the audit health.
        </p>
      )}
      {rows.length > 0 && (
        <dl className="divide-y divide-gray-100 max-w-2xl">
          {rows.map(row => (
            <div
              key={row.label}
              className="flex flex-wrap items-start gap-x-4 gap-y-1 py-2">
              <dt className="w-48 text-sm text-gray-600">{row.label}</dt>
              <dd className="flex-1 text-sm">
                <span
                  data-tone={row.tone}
                  className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${TONE_CLASSES[row.tone]}`}>
                  {row.status}
                </span>
                {row.detail && (
                  <p className="mt-1 text-gray-500">{row.detail}</p>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};

export default AuditHealthCard;
